import { cx } from '@otter-ai/den-components';
import Image from 'next/image';
import { type ComponentType, type ReactNode, useState } from 'react';
import { generateTargetStyles } from '@/shared/utils/targetStyles/generateTargetStyles';
import type { AppRowId } from '../constants/row-ids';
import { createAppTestIds } from '../test-ids';
import { EnterpriseTag } from './EnterpriseTag';
import { NewTag } from './NewTag';
import { ProTag } from './ProTag';

export type AppRowLayoutProps = {
  appRowId: AppRowId | string;
  title: string;
  description: ReactNode;
  iconUrl?: string;
  Icon?: ComponentType<{ size: number }>;
  buttons?: ReactNode;
  extendContent?: ReactNode;
  /**
   * Content that spans the full width of the row (all columns).
   * Use for banners, paywalls, or other elements that should stretch edge-to-edge.
   */
  fullWidthContent?: ReactNode;
  tags?: {
    enterpriseTag?: boolean;
    newTag?: boolean;
    proTag?: boolean;
  };
  linkedAccounts?: {
    accountName: string;
    accountId: string | number;
    button: ReactNode;
    additionalContent?: ReactNode;
  }[];
  /**
   * This function is called when the user hover's over the row for the first time.
   * The common use-case is to start fetching lazy loading modals.
   * @todo(sleahy): Create a prefetch utility for when components come into view
   */
  prefetch?: () => void;
};

/**
 * Shared layout for all apps.
 * [Figma](https://www.figma.com/design/jEoBjkGRTOV4ETZ4Qli3Bl/Account-Settings-React?node-id=63-6569&m=dev)
 */
export function AppRowLayout({
  appRowId,
  title,
  description,
  iconUrl,
  Icon,
  buttons,
  extendContent,
  fullWidthContent,
  tags,
  linkedAccounts,
  prefetch,
}: AppRowLayoutProps) {
  const [isPrefetched, setIsPrefetched] = useState(false);

  const doPrefetch = () => {
    if (prefetch && !isPrefetched) {
      setIsPrefetched(true);
      prefetch();
    }
  };
  const iconElement = Icon ? (
    <Icon size={40} />
  ) : iconUrl ? (
    <Image src={iconUrl} alt={title} width={40} height={40} />
  ) : null;

  const testIds = createAppTestIds(appRowId);

  return (
    <div
      id={appRowId}
      tabIndex={-1}
      className={cx(
        'relative py-6 grid grid-cols-[var(--space-10,40px)_1fr_auto] gap-x-6 border-b border-subtle',
        generateTargetStyles({
          xExpandCoverage: 4,
          yExpandCoverage: 0,
          disableOutline: true,
        }),
      )}
      onMouseEnter={doPrefetch}
      data-testid={testIds.row}
    >
      <div className="w-10 col-start-1 row-start-1">{iconElement}</div>
      <div className="row-start-1 col-start-2">
        <div className="flex items-center gap-2">
          <h2 className="font-semibold" data-testid={testIds.title}>
            {title}
          </h2>
          {tags?.enterpriseTag && <EnterpriseTag size="sm" />}
          {tags?.newTag && <NewTag size="sm" />}
          {tags?.proTag && <ProTag size="sm" />}
        </div>
        <div
          className="text-subtle min-h-[3rem]"
          data-testid={testIds.description}
        >
          {description}
        </div>
      </div>
      <div className="row-start-1 col-start-3 justify-self-end">{buttons}</div>
      {linkedAccounts?.map((linkedAccount, index) => (
        <AppLinkedAccountLayout
          key={linkedAccount.accountId}
          accountName={linkedAccount.accountName}
          accountId={linkedAccount.accountId}
          button={linkedAccount.button}
          data-testid={testIds.accountRow(index)}
          index={index}
          additionalContent={linkedAccount.additionalContent}
        />
      ))}
      <div className="col-start-2 col-span-2">{extendContent}</div>
      {fullWidthContent && (
        <div className="col-start-2 col-span-2">{fullWidthContent}</div>
      )}
    </div>
  );
}

export type AppLinkedAccountLayoutProps = {
  accountName: string;
  accountId: string | number;
  button: ReactNode;
  accountClassName?: string;
  buttonClassName?: string;
  'data-testid': string;
  index: number;
  additionalContent?: ReactNode;
};

export function AppLinkedAccountLayout({
  accountName,
  accountId,
  button,
  'data-testid': dataTestId,
  index,
  additionalContent,
}: AppLinkedAccountLayoutProps) {
  return (
    <div
      data-testid={dataTestId}
      data-account-id={accountId}
      className={cx(
        // The more you know: subgrid means the columns match the parents' columns
        'col-start-2 col-span-2 grid grid-cols-subgrid py-3',
        index === 0 ? 'mt-4' : 'border-t border-subtle',
      )}
    >
      <div
        data-testid={`${dataTestId}-account-name`}
        className="col-start-1 font-semibold self-center"
      >
        <span>{accountName}</span>
      </div>
      {additionalContent && (
        <div
          data-testid={`${dataTestId}-additional-content`}
          className="row-start-2"
        >
          {additionalContent}
        </div>
      )}
      <div
        data-testid={`${dataTestId}-button`}
        className="col-start-2 flex self-center justify-self-end"
      >
        {button}
      </div>
    </div>
  );
}
